import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { StarIcon, HeartIcon, ShareIcon, ShoppingCartIcon, TruckIcon, ShieldCheckIcon, RefreshCwIcon, MinusIcon, PlusIcon, CheckIcon } from 'lucide-react';
import { products } from '../data/products';
import { formatPrice, getStockStatus } from '../utils/formatters';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ProductGrid } from '../components/products/ProductGrid';
export function ProductDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = products.find(p => p.slug === slug);
  const {
    addItem
  } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  if (!product) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-1447">
        <div className="text-center" data-id="element-1448">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1449">
            Product Not Found
          </h1>
          <p className="text-gray-600 mb-4" data-id="element-1450">
            The product you're looking for doesn't exist.
          </p>
          <Link to="/products" data-id="element-1451">
            <Button variant="primary" data-id="element-1452">Browse Products</Button>
          </Link>
        </div>
      </div>;
  }
  const stockStatus = getStockStatus(product.stockQuantity);
  const relatedProducts = products.filter(p => p.categorySlug === product.categorySlug && p.id !== product.id).slice(0, 4);
  const handleAddToCart = () => {
    addItem(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };
  return <div className="min-h-screen bg-gray-50 py-6" data-id="element-1453">
      <div className="max-w-7xl mx-auto px-4" data-id="element-1454">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb" data-id="element-1455">
          <ol className="flex items-center gap-2 flex-wrap" data-id="element-1456">
            <li data-id="element-1457">
              <Link to="/" className="hover:text-primary-600" data-id="element-1458">
                Home
              </Link>
            </li>
            <li data-id="element-1459">/</li>
            <li data-id="element-1460">
              <Link to="/products" className="hover:text-primary-600" data-id="element-1461">
                Products
              </Link>
            </li>
            <li data-id="element-1462">/</li>
            <li data-id="element-1463">
              <Link to={`/products?category=${product.categorySlug}`} className="hover:text-primary-600" data-id="element-1464">
                {product.category}
              </Link>
            </li>
            <li data-id="element-1465">/</li>
            <li className="text-gray-900 font-medium truncate max-w-[200px]" data-id="element-1466">
              {product.name}
            </li>
          </ol>
        </nav>

        {/* Product section */}
        <div className="bg-white rounded-xl shadow-card p-6 mb-8" data-id="element-1467">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1468">
            {/* Images */}
            <div data-id="element-1469">
              <div className="aspect-square rounded-xl overflow-hidden bg-gray-50 mb-4" data-id="element-1470">
                <img src={product.images[selectedImage]} alt={product.name} className="w-full h-full object-cover" data-id="element-1471" />
              </div>
              {product.images.length > 1 && <div className="flex gap-3" data-id="element-1472">
                  {product.images.map((image, index) => <button key={index} onClick={() => setSelectedImage(index)} className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${selectedImage === index ? 'border-primary-600' : 'border-gray-200'}`} data-id="element-1473">
                      <img src={image} alt="" className="w-full h-full object-cover" data-id="element-1474" />
                    </button>)}
                </div>}
            </div>

            {/* Details */}
            <div data-id="element-1475">
              <div className="flex items-start justify-between gap-4" data-id="element-1476">
                <div data-id="element-1477">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-1478">{product.brand}</p>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-1479">
                    {product.name}
                  </h1>
                </div>
                <div className="flex gap-2" data-id="element-1480">
                  <button className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:text-red-500 hover:border-red-200 transition-colors" aria-label="Add to wishlist" data-id="element-1481">
                    <HeartIcon className="w-5 h-5" data-id="element-1482" />
                  </button>
                  <button className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:text-primary-600 hover:border-primary-200 transition-colors" aria-label="Share product" data-id="element-1483">
                    <ShareIcon className="w-5 h-5" data-id="element-1484" />
                  </button>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4" data-id="element-1485">
                <div className="flex items-center" data-id="element-1486">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} data-id="element-1487" />)}
                </div>
                <span className="text-sm text-gray-600" data-id="element-1488">
                  {product.rating} ({product.reviewCount.toLocaleString()}{' '}
                  reviews)
                </span>
              </div>

              {/* Price */}
              <div className="flex items-center gap-3 mb-4" data-id="element-1489">
                <span className="text-3xl font-bold text-gray-900" data-id="element-1490">
                  {formatPrice(product.price)}
                </span>
                {product.originalPrice && product.originalPrice > product.price && <>
                      <span className="text-xl text-gray-400 line-through" data-id="element-1491">
                        {formatPrice(product.originalPrice)}
                      </span>
                      <Badge variant="error" data-id="element-1492">-{product.discount}% OFF</Badge>
                    </>}
              </div>

              {/* Stock status */}
              <div className="flex items-center gap-2 mb-6" data-id="element-1493">
                <span className={`font-medium ${stockStatus.color}`} data-id="element-1494">
                  {stockStatus.label}
                </span>
                {product.inStock && <span className="text-sm text-gray-500" data-id="element-1495">
                    ({product.stockQuantity} available)
                  </span>}
              </div>

              {/* Short description */}
              <p className="text-gray-600 mb-6" data-id="element-1496">{product.shortDescription}</p>

              {/* Quantity selector */}
              <div className="flex items-center gap-4 mb-6" data-id="element-1497">
                <span className="text-sm font-medium text-gray-700" data-id="element-1498">
                  Quantity:
                </span>
                <div className="flex items-center gap-2" data-id="element-1499">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors" aria-label="Decrease quantity" data-id="element-1500">
                    <MinusIcon className="w-4 h-4" data-id="element-1501" />
                  </button>
                  <span className="w-12 text-center font-medium text-lg" data-id="element-1502">
                    {quantity}
                  </span>
                  <button onClick={() => setQuantity(Math.min(product.stockQuantity, quantity + 1))} className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors" aria-label="Increase quantity" data-id="element-1503">
                    <PlusIcon className="w-4 h-4" data-id="element-1504" />
                  </button>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 mb-6" data-id="element-1505">
                <Button variant="primary" size="lg" className="flex-1" leftIcon={addedToCart ? <CheckIcon className="w-5 h-5" data-id="element-1507" /> : <ShoppingCartIcon className="w-5 h-5" data-id="element-1508" />} onClick={handleAddToCart} disabled={!product.inStock} data-id="element-1506">
                  {addedToCart ? 'Added to Cart!' : 'Add to Cart'}
                </Button>
                <Link to="/checkout" className="flex-1" data-id="element-1509">
                  <Button variant="outline" size="lg" fullWidth disabled={!product.inStock} data-id="element-1510">
                    Buy Now
                  </Button>
                </Link>
              </div>

              {/* Features */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-6 border-t border-gray-100" data-id="element-1511">
                <div className="flex items-center gap-3" data-id="element-1512">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center" data-id="element-1513">
                    <TruckIcon className="w-5 h-5 text-primary-600" data-id="element-1514" />
                  </div>
                  <div data-id="element-1515">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1516">
                      Free Delivery
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1517">
                      Orders over PKR 5,000
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-1518">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1519">
                    <ShieldCheckIcon className="w-5 h-5 text-green-600" data-id="element-1520" />
                  </div>
                  <div data-id="element-1521">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1522">
                      Warranty
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1523">1 Year Warranty</p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-1524">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center" data-id="element-1525">
                    <RefreshCwIcon className="w-5 h-5 text-primary-600" data-id="element-1526" />
                  </div>
                  <div data-id="element-1527">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1528">
                      Easy Returns
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1529">7 Days Return</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product details tabs */}
        <div className="bg-white rounded-xl shadow-card p-6 mb-8" data-id="element-1530">
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1531">
            Product Details
          </h2>

          <div className="prose max-w-none text-gray-600 mb-6" data-id="element-1532">
            <p data-id="element-1533">{product.description}</p>
          </div>

          {/* Specifications */}
          <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1534">
            Specifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-1535">
            {Object.entries(product.specifications).map(([key, value]) => <div key={key} className="flex border-b border-gray-100 py-2" data-id="element-1536">
                <span className="text-gray-500 w-1/2" data-id="element-1537">{key}</span>
                <span className="text-gray-900 font-medium w-1/2" data-id="element-1538">{value}</span>
              </div>)}
          </div>

          {/* Features */}
          <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1539">
            Key Features
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-1540">
            {product.features.map((feature, index) => <li key={index} className="flex items-center gap-2 text-gray-600" data-id="element-1541">
                <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1542" />
                {feature}
              </li>)}
          </ul>
        </div>

        {/* Related products */}
        {relatedProducts.length > 0 && <div className="mb-8" data-id="element-1543">
            <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1544">
              Related Products
            </h2>
            <ProductGrid products={relatedProducts} columns={4} data-id="element-1545" />
          </div>}
      </div>
    </div>;
}